/**
 * 布置学习的"什么时候做"（docs/API.md 2.4）：布置 = 新建一个打开学习内容的日程，可以重复。
 * - 截止（默认）：哪天、几点前做完（time_mode = due）；
 * - 时段：几点开始、做多久，切换过来时用 suggest-slot 预填放学后不重叠的时段；
 * - 放进时间段：用某个已有日程那天的时段（如"写作业"）。
 */
import { Check, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, type Child, fetchAgenda, type LocalCalendar, select, useCalendar, useStore, WEEKDAY_NAMES } from "../core";
import { categoryStyle, C, ChoiceChip, NumberStepper, PillSegment, ValueRow } from "../ui";
import { assignableSlots, dayLabel, type SlotRef, slotKey } from "../pages/learn/activity";
import { buildRRule, describeRepeat, REPEAT_KINDS, toggleDay, withKind, type RepeatRule } from "../pages/schedule/repeat";

export type WhenMode = "due" | "time" | "slot";

export interface When {
  mode: WhenMode;
  /** 截止 / 时段：哪天做（重复的从这天开始） */
  date: string;
  /** 放进时间段：选中的时间段 */
  slot: string | null;
  /** 时段：开始时间 HH:mm 和时长 */
  startTime: string;
  duration: number;
  /** 截止：几点前做完 */
  dueTime: string;
  repeat: RepeatRule;
}

export const WHEN_MODES = [
  ["due", "截止"],
  ["time", "时段"],
  ["slot", "放进时间段"],
] as const;

/** 未来 7 天可以挂的时间段（切换孩子时重新拉取） */
export function useSlots(child: Child | null | undefined): { slots: SlotRef[]; loaded: boolean } {
  const cal = useCalendar();
  const today = cal.today();
  const [loaded, setLoaded] = useState(false);
  const days = useStore(
    (s) => Array.from({ length: 7 }, (_, i) => (child ? select.agendaDay(s, child.id, cal.addDays(today, i)) : undefined)),
    (a, b) => a.length === b.length && a.every((x, i) => x === b[i]),
  );
  const slots = useMemo(() => assignableSlots(days, Date.now()), [days]);
  useEffect(() => {
    if (!child) return;
    let alive = true;
    setLoaded(false);
    void fetchAgenda(child.id, today, cal.addDays(today, 6)).finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [child?.id, today, cal]);
  return { slots, loaded };
}

/** 截止时间（按时完成另加 onTimeBonus 分）：截止 = 那个时间；时段 / 时间段 = 结束时 */
export function whenDueAt(cal: LocalCalendar, w: When, slot: SlotRef | null): number | null {
  if (w.mode === "slot") return slot ? slot.item.endAt : null;
  if (w.mode === "time") {
    const start = cal.ms(w.date, w.startTime);
    return start == null ? null : start + w.duration * 60_000;
  }
  return cal.ms(w.date, w.dueTime);
}

/** POST children/{id}/events 的时间字段（学习日程） */
export function whenEvent(w: When, slot: SlotRef | null) {
  const date = w.mode === "slot" && slot ? slot.date : w.date;
  const time =
    w.mode === "slot" && slot
      ? { timeMode: "range", startTime: slot.item.startTime, durationMin: Math.max(1, Math.round((slot.item.endAt - slot.item.startAt) / 60_000)) }
      : w.mode === "time"
        ? { timeMode: "range", startTime: w.startTime, durationMin: w.duration }
        : { timeMode: "due", startTime: w.dueTime, durationMin: 0 };
  return { startDate: date, ...time, rrule: buildRRule(w.repeat, date), category: w.mode === "slot" && slot ? slot.item.category : "after_school" };
}

/** 能保存：放进时间段时要选中一个 */
export function whenValid(w: When, slot: SlotRef | null): boolean {
  if (w.repeat.kind !== "none" && (w.repeat.kind === "weekly" || w.repeat.kind === "biweekly") && w.repeat.days.length === 0) return false;
  if (w.mode === "slot") return !!slot;
  if (w.mode === "time") return /^\d{2}:\d{2}$/.test(w.startTime) && w.duration >= 5;
  return !!w.date && /^\d{2}:\d{2}$/.test(w.dueTime);
}

interface Suggestion {
  date: string;
  startTime: string;
  durationMin: number;
}

/** 放学后、不和日程重叠、不早于现在的建议时段 */
export async function suggestSlot(childId: string, date: string, duration: number, exclude?: string): Promise<Suggestion | null> {
  try {
    return await api.get<Suggestion>(`children/${childId}/suggest-slot`, { date, duration_min: duration, ...(exclude ? { exclude } : {}) });
  } catch {
    return null;
  }
}

export function WhenCard({
  child,
  value,
  onChange,
  slots,
  slotsLoaded,
  excludeId,
}: {
  child: Child | null | undefined;
  value: When;
  onChange: (w: When) => void;
  slots: SlotRef[];
  slotsLoaded: boolean;
  excludeId?: string;
}) {
  const cal = useCalendar();
  const today = cal.today();
  const [suggesting, setSuggesting] = useState(false);

  const suggest = async (w: When) => {
    if (!child) return;
    setSuggesting(true);
    const s = await suggestSlot(child.id, w.date, w.duration, excludeId);
    setSuggesting(false);
    if (s) onChange({ ...w, mode: "time", date: s.date, startTime: s.startTime, duration: s.durationMin });
  };

  const switchMode = (mode: WhenMode) => {
    const w = { ...value, mode };
    onChange(w);
    // 第一次切到"安排时间"：预填建议时段
    if (mode === "time" && value.mode !== "time") void suggest(w);
  };

  return (
    <div className="learn-card">
      <span className="learn-label">什么时候做</span>
      <PillSegment items={WHEN_MODES} value={value.mode} onChange={switchMode} height={34} stretch background={C.bg} />
      {value.mode === "slot" ? (
        <div className="learn-slots" data-testid="slots">
          {!slotsLoaded && slots.length === 0 && <p className="learn-note">正在读取时间表…</p>}
          {slotsLoaded && slots.length === 0 && <p className="learn-note">未来 7 天没有时间段，可以改成截止或时段</p>}
          {slots.map((s) => (
            <SlotButton key={slotKey(s)} s={s} on={slotKey(s) === value.slot} label={dayLabel(cal, s.date)} onClick={() => onChange({ ...value, slot: slotKey(s) })} />
          ))}
        </div>
      ) : (
        <>
          <ValueRow title={value.repeat.kind === "none" ? "日期" : "开始日期"} height={44}>
            <input type="date" className="learn-date" value={value.date} min={today} onChange={(e) => e.target.value && onChange({ ...value, date: e.target.value })} aria-label="日期" />
          </ValueRow>
          {value.mode === "due" && (
            <ValueRow title="几点前做完" height={44}>
              <input type="time" className="learn-date" value={value.dueTime} onChange={(e) => e.target.value && onChange({ ...value, dueTime: e.target.value })} aria-label="截止时间" />
            </ValueRow>
          )}
          {value.mode === "time" && (
            <>
              <ValueRow title="开始" height={44}>
                <input type="time" className="learn-date" value={value.startTime} onChange={(e) => e.target.value && onChange({ ...value, startTime: e.target.value })} aria-label="开始时间" />
                <button type="button" className="kp-icon-btn" title="建议时段（放学后、不和日程重叠）" aria-label="建议时段" disabled={suggesting} onClick={() => void suggest(value)}>
                  <Sparkles size={16} strokeWidth={2.4} />
                </button>
              </ValueRow>
              <ValueRow title="做多久" height={52}>
                <NumberStepper value={value.duration} min={5} max={240} step={5} onChange={(v) => onChange({ ...value, duration: v })} />
              </ValueRow>
            </>
          )}
          <p className="learn-note">{value.mode === "due" ? "时间表上显示截止时间，孩子在截止前做完就行。" : "时间表上按这个时段显示；点星号按钮可以重新建议放学后的空档。"}</p>
        </>
      )}
      <RepeatChips value={value.repeat} startDate={value.mode === "slot" ? (slots.find((x) => slotKey(x) === value.slot)?.date ?? value.date) : value.date} onChange={(repeat) => onChange({ ...value, repeat })} />
    </div>
  );
}

/** 重复：不重复 / 每天 / 每周几……（重复的每天生成一条学习记录） */
function RepeatChips({ value, startDate, onChange }: { value: RepeatRule; startDate: string; onChange: (r: RepeatRule) => void }) {
  const cal = useCalendar();
  const weekly = value.kind === "weekly" || value.kind === "biweekly";
  return (
    <>
      <div className="learn-due-head">
        <span>重复</span>
        <span className="learn-due-value">{value.kind === "none" ? "只做一次" : describeRepeat(value, startDate)}</span>
      </div>
      <div className="learn-chips">
        {REPEAT_KINDS.map(([k, label]) => (
          <ChoiceChip key={k} selected={value.kind === k} onClick={() => onChange(withKind(value, k, cal.weekday(startDate)))}>
            {label}
          </ChoiceChip>
        ))}
      </div>
      {weekly && (
        <div className="learn-chips">
          {[1, 2, 3, 4, 5, 6, 7].map((d) => (
            <ChoiceChip key={d} selected={value.days.includes(d)} onClick={() => onChange(toggleDay(value, d))}>
              {WEEKDAY_NAMES[d]}
            </ChoiceChip>
          ))}
        </div>
      )}
    </>
  );
}

function SlotButton({ s, on, label, onClick }: { s: SlotRef; on: boolean; label: string; onClick: () => void }) {
  const st = categoryStyle(s.item.category);
  const time = s.item.dayCount && s.item.dayCount > 1 ? `${s.item.startTime} 开始` : s.item.startTime;
  return (
    <button type="button" className={`learn-slot ${on ? "is-on" : ""}`} style={{ background: on ? st.tint : undefined }} onClick={onClick} aria-pressed={on}>
      <span className="kp-num">
        {label} {time}
      </span>
      <strong>{s.item.title}</strong>
      {on && <Check size={16} strokeWidth={3} />}
    </button>
  );
}
